<template>
  <section class="personal-center-panel member-cert-panel">
    <h2>电子会员证</h2>

    <div v-if="!front.isActiveMember" class="service-notice warning">
      <strong>暂不可申请</strong>
      <span>仅有效正式会员可申请电子会员证。请先完成入会与会费确认。</span>
    </div>

    <template v-else>
      <div class="service-notice">
        <strong>申请说明</strong>
        <span>上传一寸免冠证件照（JPG/PNG，≤5MB）→ 秘书处审核 → 通过后可下载电子会员证（试用版式）。</span>
      </div>

      <div v-if="loading" class="service-hint">正在加载电子证状态…</div>

      <template v-else>
        <div class="cert-status-panel" :class="statusClass">
          <strong>{{ statusTitle }}</strong>
          <span>{{ statusDescription }}</span>
          <p v-if="cert.reviewComment && cert.certStatus === 'REJECTED'" class="cert-review-comment">
            驳回原因：{{ cert.reviewComment }}
          </p>
          <p v-if="cert.memberNo || cert.certNo" class="cert-no">会员编号（会号）：{{ cert.memberNo || cert.certNo }}</p>
        </div>

        <div v-if="canUpload" class="cert-upload">
          <label class="application-file-picker">
            <span>制证照片（JPG/PNG，≤5MB）</span>
            <input ref="photoInput" type="file" accept=".jpg,.jpeg,.png" @change="selectPhoto" />
            <small v-if="pendingFile">已选择：{{ pendingFile.name }}</small>
          </label>
          <div class="inline-actions">
            <button
              v-if="pendingFile"
              type="button"
              class="secondary-action compact"
              @click="clearPhoto"
            >重新选择</button>
            <button
              type="button"
              class="primary-action compact"
              :disabled="!pendingFile || submitting"
              @click="submitPhoto"
            >{{ submitting ? '正在提交...' : '提交审核' }}</button>
          </div>
        </div>

        <div v-if="cert.downloadAvailable" class="inline-actions">
          <button type="button" class="primary-action compact" :disabled="downloading" @click="download">
            {{ downloading ? '正在下载...' : '下载电子会员证 PDF' }}
          </button>
        </div>

        <p v-if="message" class="service-hint">{{ message }}</p>
      </template>
    </template>
  </section>
</template>

<script>
import { getMyMemberCertificate, uploadMemberCertPhoto, downloadMemberCertificate } from '@/api/paleo'

export default {
  name: 'PersonalMemberCertPanel',
  inject: ['front'],
  data() {
    return {
      loading: false,
      submitting: false,
      downloading: false,
      pendingFile: null,
      message: '',
      cert: {
        certStatus: 'NONE',
        downloadAvailable: false
      }
    }
  },
  computed: {
    /** 透传 inject 实例上的计算属性，便于可靠 watch（profile 异步到达后补拉） */
    isActiveMember() {
      return !!(this.front && this.front.isActiveMember)
    },
    canUpload() {
      const s = this.cert.certStatus
      return s === 'NONE' || s === 'REJECTED'
    },
    statusTitle() {
      return {
        NONE: '尚未申请',
        PENDING: '审核中',
        APPROVED: '已生效',
        REJECTED: '已驳回'
      }[this.cert.certStatus] || this.cert.certStatus
    },
    statusDescription() {
      return {
        NONE: '请上传制证照片提交审核。',
        PENDING: '照片已提交，秘书处正在审核，请耐心等待。',
        APPROVED: '电子会员证已生效，可下载使用（试用版式）。',
        REJECTED: '请根据驳回原因更换照片后重新提交。'
      }[this.cert.certStatus] || ''
    },
    statusClass() {
      return {
        NONE: 'info',
        PENDING: 'pending',
        APPROVED: 'success',
        REJECTED: 'warning'
      }[this.cert.certStatus] || 'info'
    }
  },
  watch: {
    // loadSelfService 异步：面板 created 时往往还不是 ACTIVE，原先只在 created 拉一次会永远停在「尚未申请」
    isActiveMember: {
      immediate: true,
      handler(active) {
        if (active) {
          this.loadCert()
        } else {
          this.cert = { certStatus: 'NONE', downloadAvailable: false }
          this.message = ''
        }
      }
    }
  },
  methods: {
    async loadCert() {
      this.loading = true
      this.message = ''
      try {
        const res = await getMyMemberCertificate()
        const data = (res && res.data) || res || {}
        this.cert = {
          certStatus: data.certStatus || 'NONE',
          downloadAvailable: !!data.downloadAvailable,
          certNo: data.certNo || null,
          memberNo: data.memberNo || data.certNo || null,
          photoUrl: data.photoUrl || null,
          reviewComment: data.reviewComment || null,
          pdfGenerateStatus: data.pdfGenerateStatus || null,
          certId: data.certId || null,
          membershipActive: data.membershipActive
        }
      } catch (error) {
        this.message = error.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    selectPhoto(event) {
      const file = event.target.files[0]
      if (!file) return
      const ext = (file.name || '').split('.').pop().toLowerCase()
      if (!['jpg', 'jpeg', 'png'].includes(ext)) {
        this.message = '制证照片请上传 JPG 或 PNG 文件'
        event.target.value = ''
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        this.message = '制证照片不能超过 5MB'
        event.target.value = ''
        return
      }
      this.pendingFile = file
      this.message = ''
    },
    clearPhoto() {
      this.pendingFile = null
      if (this.$refs.photoInput) this.$refs.photoInput.value = ''
    },
    async submitPhoto() {
      if (!this.pendingFile) return
      this.submitting = true
      this.message = ''
      try {
        await uploadMemberCertPhoto(this.pendingFile)
        this.clearPhoto()
        this.message = '照片已提交，等待审核'
        await this.loadCert()
      } catch (error) {
        this.message = error.message || '提交失败'
      } finally {
        this.submitting = false
      }
    },
    async download() {
      this.downloading = true
      this.message = ''
      try {
        const blob = await downloadMemberCertificate()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `会员证_${this.cert.certNo || 'download'}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      } catch (error) {
        this.message = error.message || '下载失败'
      } finally {
        this.downloading = false
      }
    }
  }
}
</script>

<style scoped>
.member-cert-panel .cert-status-panel {
  margin: 12px 0;
  padding: 12px 14px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  line-height: 1.6;
}
.member-cert-panel .cert-status-panel.info {
  background: #f4f4f5;
  border: 1px solid #e9e9eb;
  color: #606266;
}
.member-cert-panel .cert-status-panel.pending {
  background: #fdf6ec;
  border: 1px solid #faecd8;
  color: #b88230;
}
.member-cert-panel .cert-status-panel.success {
  background: #f0f9eb;
  border: 1px solid #d1edc4;
  color: #1d6b3a;
}
.member-cert-panel .cert-status-panel.warning {
  background: #fef0f0;
  border: 1px solid #fde2e2;
  color: #f56c6c;
}
.member-cert-panel .cert-review-comment {
  margin: 0;
  font-weight: 600;
}
.member-cert-panel .cert-no {
  margin: 0;
  font-weight: 700;
}
.member-cert-panel .cert-upload {
  margin-top: 12px;
  padding: 12px 14px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  background: #fafbfc;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
